import { equal } from "dinero.js"

import { ProductGroups } from "../../../../../../modules/merchant/admin/products/productsTypes"
import { toDinero } from "../../../../../../modules/currency/currencyLib"
import { MonetaryAmount } from "../../../../../../modules/currency/currencyTypes"
import { DeliveryTimesField } from "../../form/formTypes"

type PriceCompareOptions = Readonly<{
    priceListIncludeVat: boolean
}>

// Compares by value, not by representation: the order line and the catalog come from different
// endpoints, so the same amount can arrive with a different scale (2500/2 vs 25000/3).
export function moneyEquals(a?: MonetaryAmount, b?: MonetaryAmount): boolean {
    if (!a || !b) return false
    if (a.currency !== b.currency) return false
    // Identical representation is the common case; short-circuiting it also keeps an
    // unsupported currency code (toDinero throws on those) out of the comparison.
    if (a.amount === b.amount && a.scale === b.scale) return true

    return equal(toDinero(a), toDinero(b))
}

type PriceWithVat = Readonly<{
    price?: MonetaryAmount
    priceInclVat?: MonetaryAmount
    priceExclVat?: MonetaryAmount
}>

// The catalog exposes the price in both VAT bases; compare against the one that matches the
// selected price list so the stored line price and the catalog price share the same basis.
function pickComparablePrice(value: PriceWithVat, opts?: PriceCompareOptions): MonetaryAmount | undefined {
    if (opts?.priceListIncludeVat) {
        return value.priceInclVat ?? value.price
    }

    return value.priceExclVat ?? value.price
}

export function buildPriceMap(list?: ProductGroups[], opts?: PriceCompareOptions): Map<number, MonetaryAmount> {
    const map = new Map<number, MonetaryAmount>()
    for (const group of list ?? []) {
        for (const p of group?.products ?? []) {
            const price = pickComparablePrice(p, opts) ?? null
            if (price) {
                map.set(p.id, price)
            }
        }
    }
    return map
}

export type SelectedLine = Readonly<{
    id: number
    name: string
    quantity: number
    price: MonetaryAmount
    priceListId?: number
}>

export function collectSelectedLines(deliveries?: DeliveryTimesField): SelectedLine[] {
    const out: SelectedLine[] = []
    for (const slot of deliveries?.deliveryTimes ?? []) {
        for (const line of slot?.orderLines ?? []) {
            if (!line?.isExtraService && typeof line?.id === "number" && line?.price) {
                out.push({
                    id: line.id,
                    name: line.name,
                    quantity: line.quantity,
                    price: line.price,
                    priceListId: line.priceListId,
                })
            }
        }
    }
    return out
}

export enum PriceChangeReason {
    PRICE_CHANGED = "PRICE_CHANGED",
    MISSING_IN_PRICE_LIST = "MISSING_IN_PRICE_LIST",
    OTHER_PRICE_LIST = "OTHER_PRICE_LIST",
}

export type PriceChange = Readonly<{
    id: number
    name: string
    quantity: number
    reason: PriceChangeReason
    // The price stored on the order line; for PRICE_CHANGED it is what "Update" replaces.
    orderPrice: MonetaryAmount
    catalogPrice?: MonetaryAmount
}>

// The same product can sit in several delivery slots — the dialog lists it once with the summed
// quantity. The stored price is part of the key: the same product bought at two different prices
// is two different changes to explain.
function dedupeKey(id: number, price: MonetaryAmount): string {
    return `${id}:${price.amount}:${price.scale}:${price.currency}`
}

// Explains every order line the catalog no longer agrees with. A line missing from the catalog can
// mean several things on the backend (deactivated, unavailable on the delivery date, staff-only and
// filtered out), so we only distinguish what the data proves: a line stamped with another price
// list id is reported as such, everything else as "not in the selected price list".
export function collectPriceChanges(
    priceById: Map<number, MonetaryAmount>,
    selected: ReadonlyArray<SelectedLine>,
    selectedPriceListId?: number,
): PriceChange[] {
    const byKey = new Map<string, PriceChange>()

    for (const { id, name, quantity, price, priceListId } of selected) {
        const current = priceById.get(id)

        let reason: PriceChangeReason
        if (current) {
            if (moneyEquals(price, current)) continue
            reason = PriceChangeReason.PRICE_CHANGED
        } else if (
            typeof priceListId === "number" &&
            typeof selectedPriceListId === "number" &&
            priceListId !== selectedPriceListId
        ) {
            reason = PriceChangeReason.OTHER_PRICE_LIST
        } else {
            reason = PriceChangeReason.MISSING_IN_PRICE_LIST
        }

        const key = dedupeKey(id, price)
        const existing = byKey.get(key)
        if (existing) {
            byKey.set(key, { ...existing, quantity: existing.quantity + quantity })
            continue
        }

        byKey.set(key, {
            id,
            name,
            quantity,
            reason,
            orderPrice: price,
            ...(reason === PriceChangeReason.PRICE_CHANGED ? { catalogPrice: current } : {}),
        })
    }

    return Array.from(byKey.values())
}

export type PriceChangeGroup = Readonly<{
    reason: PriceChangeReason
    items: ReadonlyArray<PriceChange>
}>

const PRICE_CHANGE_REASON_ORDER: ReadonlyArray<PriceChangeReason> = [
    PriceChangeReason.PRICE_CHANGED,
    PriceChangeReason.MISSING_IN_PRICE_LIST,
    PriceChangeReason.OTHER_PRICE_LIST,
]

export function groupPriceChangesByReason(changes: ReadonlyArray<PriceChange>): ReadonlyArray<PriceChangeGroup> {
    const groups: PriceChangeGroup[] = []

    for (const reason of PRICE_CHANGE_REASON_ORDER) {
        const items = changes.filter((change) => change.reason === reason)
        if (items.length > 0) {
            groups.push({ reason, items })
        }
    }

    return groups
}

export function updatePricesInDeliveries(
    deliveries: DeliveryTimesField,
    priceById: Map<number, MonetaryAmount>,
): { updated: DeliveryTimesField; changed: boolean } {
    let changed = false

    const updated = {
        ...deliveries,
        deliveryTimes: deliveries.deliveryTimes.map((slot) => {
            let slotChanged = false
            const updatedOrderLines = (slot.orderLines ?? []).map((line) => {
                if (line?.isExtraService || typeof line?.id !== "number") return line

                const currentPrice = priceById.get(line.id)
                const oldPrice = line.price as MonetaryAmount | undefined
                if (!currentPrice || !oldPrice) return line
                if (moneyEquals(oldPrice, currentPrice)) return line

                slotChanged = true
                changed = true
                return { ...line, price: currentPrice }
            })
            return slotChanged ? { ...slot, orderLines: updatedOrderLines } : slot
        }),
    }

    return { updated, changed }
}

export function getProductQuantityFromDeliveries(deliveries: DeliveryTimesField, productId: number): number {
    if (!deliveries?.activeTime) return 0

    const activeDelivery = deliveries.deliveryTimes.find((d) => d.time === deliveries.activeTime)
    if (!activeDelivery) return 0

    const product = activeDelivery.orderLines.find((item) => item.id === productId && !item.isExtraService)

    return product?.quantity || 0
}
